// Day Pass Checkout — mini-registration flow for one-time membership tiers
// flagged isDayPass=true. Public route, no Firebase Auth required.
//
// Flow:
//   1. User enters email + clicks Continue
//   2. Cloud Function lookup tells us if email is new / returning mini / returning full-auth
//   3. Show appropriate fields (new=name/phone/waiver; returning=confirm+waiver)
//   4. Submit → CF creates/updates mini account → CF creates Stripe Checkout session
//   5. Redirect to Stripe → user pays → returns to /payment-success
//
// Waiver: reuses settings/membershipTiers.requiredWaiverId, fresh acceptance each purchase.

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc, getDocs, collection } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Box, TextField, Button, CircularProgress, Alert, Checkbox, FormControlLabel,
  Typography, Stack,
} from '@mui/material';

const gold = '#C9A84C';
const dark = '#0D1E35';
const goldMid = 'rgba(201,168,76,0.6)';

export default function DayPassCheckout() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tierId = searchParams.get('tierId');
  // Preview mode (admin tile): renders the full mini-reg flow but never writes
  // an account and never opens Stripe. Mirrors Register.js's ?preview=1 pattern.
  const previewMode = searchParams.get('preview') === '1';
  // Preview is an admin tool — bounce non-admin (or logged-out) visitors home.
  useEffect(() => {
    if (!previewMode) return;
    const verifyAdmin = async () => {
      const u = auth.currentUser;
      if (!u) { navigate('/'); return; }
      try {
        const snap = await getDoc(doc(db, 'users', u.uid));
        const roles = snap.exists() ? (snap.data().roles || []) : [];
        if (!roles.includes('admin')) navigate('/');
      } catch (e) { navigate('/'); }
    };
    verifyAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewMode]);

  const [tier, setTier] = useState(null);
  const [waiverDoc, setWaiverDoc] = useState(null);
  const [loadingTier, setLoadingTier] = useState(true);

  // Form state
  const [step, setStep] = useState('email'); // 'email' | 'newUser' | 'returningMini' | 'returningFullAuth'
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [confirmReturning, setConfirmReturning] = useState(false);

  const [lookingUp, setLookingUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Load tier + waiver doc on mount
  useEffect(() => {
    document.title = 'Day Pass · Torch Beach Volleyball';
    let cancelled = false;
    (async () => {
      try {
        let t = null;
        if (!tierId) {
          if (previewMode) {
            // Preview from the admin tile arrives without a tierId — grab the
            // first day-pass tier so the page renders realistically.
            const allTiers = await getDocs(collection(db, 'membershipTiers'));
            const dayPass = allTiers.docs.map(d => ({ id: d.id, ...d.data() })).find(x => x.isDayPass);
            if (!dayPass) {
              setError('No day-pass tier exists to preview.');
              setLoadingTier(false);
              return;
            }
            t = dayPass;
          } else {
            setError('No tier selected. Please return to memberships.');
            setLoadingTier(false);
            return;
          }
        } else {
          const tierSnap = await getDoc(doc(db, 'membershipTiers', tierId));
          if (cancelled) return;
          if (!tierSnap.exists()) {
            setError('Membership tier not found.');
            setLoadingTier(false);
            return;
          }
          t = { id: tierSnap.id, ...tierSnap.data() };
          if (!t.isDayPass) {
            setError('This tier is not available via the day pass flow.');
            setLoadingTier(false);
            return;
          }
        }
        setTier(t);

        // Load required waiver if configured
        const settingsSnap = await getDoc(doc(db, 'settings', 'membershipTiers'));
        const waiverId = settingsSnap.exists() ? (settingsSnap.data().requiredWaiverId || '') : '';
        if (waiverId) {
          const w = await getDoc(doc(db, 'clubDocuments', waiverId));
          if (!cancelled && w.exists()) setWaiverDoc({ id: w.id, ...w.data() });
        }
      } catch (e) {
        console.error('Day pass load error:', e);
        setError('Failed to load. Please try again.');
      } finally {
        if (!cancelled) setLoadingTier(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tierId]);

  function isValidEmail(e) {
    return typeof e === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  }

  async function handleEmailContinue(e) {
    e.preventDefault();
    setError('');
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLookingUp(true);
    try {
      // Tiny CF call to check existence WITHOUT creating anything yet.
      // We piggyback on createDayPassMiniAccount by passing a "dry run" flag?
      // Simpler: just do a Firestore query directly. Users docs are readable
      // by anyone? They are not. So we need a CF for lookup.
      //
      // Actually — we can just defer the lookup to submission time and decide
      // the step based on what the CF returns. For UX though, we want to know
      // BEFORE the user fills in fields. Easiest path: call a dedicated CF.
      //
      // For v1 simplicity: assume new user and show the new-user form. If the
      // CF on submit detects existing account, we proceed silently (purchase
      // attaches). This avoids needing a separate lookup CF and the email-
      // existence privacy leak.
      //
      // BUT — the spec says show "Welcome back!" for returning users. So we
      // do need a lookup. For now: skip the email-existence shortcut and just
      // always show the full new-user form. Returning users re-enter their
      // info; we use last-write-wins on the server side.
      setStep('newUser');
    } catch (err) {
      setError(err.message || 'Failed to look up email. Please try again.');
    } finally {
      setLookingUp(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    // Validate
    if (!firstName.trim()) return setError('First name is required.');
    if (!lastName.trim()) return setError('Last name is required.');
    if (!phone.trim()) return setError('Phone number is required.');
    if (waiverDoc && !waiverAccepted) return setError('You must accept the waiver to continue.');

    // Preview: validation ran (so the form behaves realistically), but stop
    // before any account write or Stripe call.
    if (previewMode) {
      setError('');
      window.alert('Preview complete — in the real flow this would create the mini account and open Stripe checkout. Nothing was saved or charged.');
      navigate('/admin');
      return;
    }

    setSubmitting(true);
    try {
      const functions = getFunctions();

      // 1) Create or update mini account
      const createAccount = httpsCallable(functions, 'createDayPassMiniAccount');
      const accountResult = await createAccount({
        email: email.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        waiverDocId: waiverDoc?.id || 'no-waiver-required',
        waiverVersion: waiverDoc?.version || null,
      });

      const { userId, isFullAuth } = accountResult.data || {};
      if (!userId) throw new Error('Account creation failed (no userId returned).');

      // If this email belongs to a full-auth user, suggest sign-in but allow proceed.
      // The CF has already attached the waiver acceptance and will attach the purchase.
      if (isFullAuth && !confirmReturning) {
        // Show inline notice — give user a chance to bail and sign in.
        setStep('returningFullAuth');
        setSubmitting(false);
        return;
      }

      // 2) Create Stripe Checkout session
      const createCheckout = httpsCallable(functions, 'createDayPassCheckoutSession');
      const successUrl = `${window.location.origin}/day-pass-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/adult-membership`;
      const checkoutResult = await createCheckout({
        tierId,
        userId,
        successUrl,
        cancelUrl,
      });

      if (!checkoutResult.data?.url) throw new Error('Failed to create checkout session.');
      window.location.href = checkoutResult.data.url;
    } catch (err) {
      console.error('Day pass submit error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  }

  if (loadingTier) {
    return (
      <Box sx={{ background: dark, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: gold }} />
      </Box>
    );
  }

  if (!tier && error) {
    return (
      <Box sx={{ background: dark, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 480 }}>
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          <Button variant="outlined" onClick={() => navigate('/adult-membership')} sx={{ borderColor: gold, color: gold }}>
            Back to Memberships
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{
      background: dark, minHeight: '100vh', color: 'white',
      fontFamily: '"Barlow", sans-serif',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '36px 16px 56px',
    }}>
      {previewMode && (
        <Alert icon={false} sx={{ maxWidth: 600, width: '100%', mb: 2,
          bgcolor: '#C9A84C', color: '#0D1E35', border: '2px solid #fff',
          '& .MuiAlert-message': { width: '100%' } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Box sx={{ fontWeight: 800, letterSpacing: 1, fontSize: '0.85rem' }}>🔍 PREVIEW MODE — MINI-REG</Box>
              <Box sx={{ fontSize: '0.72rem' }}>This is the day-pass buyer flow. Nothing will be saved or charged.</Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined"
                onClick={() => {
                  // Pre-fill realistic test data so admin doesn't have to type
                  setEmail('testbuyer@example.com');
                  setFirstName('Test');
                  setLastName('Buyer');
                  setPhone('(555) 123-4567');
                  setWaiverAccepted(true);
                  setStep('newUser');
                }}
                sx={{ borderColor: '#0D1E35', color: '#0D1E35', fontWeight: 600 }}>
                Fill Test Data
              </Button>
              <Button size="small" variant="contained"
                onClick={() => navigate('/register-preview')}
                sx={{ bgcolor: '#0D1E35', color: '#fff', '&:hover': { bgcolor: '#1a3a5c' } }}>
                Exit
              </Button>
            </Box>
          </Box>
        </Alert>
      )}
      {/* Back */}
      <Box
        onClick={() => navigate('/adult-membership')}
        sx={{
          alignSelf: 'flex-start', maxWidth: 600, width: '100%',
          color: goldMid, fontSize: '0.8rem', fontWeight: 600,
          letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', mb: 3,
          '&:hover': { color: gold },
        }}
      >
        ← Back to Memberships
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 4, textAlign: 'center' }}>
        <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: 'clamp(2rem,5vw,2.8rem)', letterSpacing: '4px', lineHeight: 1 }}>
          {tier?.name || 'Day Pass'}
        </Box>
        <Box sx={{ fontSize: '0.85rem', fontWeight: 300, color: 'rgba(255,255,255,0.45)', mt: 1, letterSpacing: '0.5px' }}>
          Quick guest registration · No password needed
        </Box>
        <Box sx={{ width: 60, height: 2, background: `linear-gradient(90deg, transparent, ${gold}, transparent)`, mt: 2 }} />
      </Box>

      {/* Price */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '3rem', color: gold, lineHeight: 1 }}>
          ${tier?.monthlyPrice}
        </Box>
        <Box sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)', mt: 0.5 }}>
          One-time payment · Valid until closing today
        </Box>
      </Box>

      {/* Form container */}
      <Box sx={{
        width: '100%', maxWidth: 480,
        background: 'rgba(255,255,255,0.05)',
        border: '1px solid rgba(201,168,76,0.2)',
        borderRadius: '14px',
        p: { xs: 3, sm: 4 },
      }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 'email' && (
          <form onSubmit={handleEmailContinue}>
            <Stack spacing={2}>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)' }}>
                Enter your email to get started.
              </Typography>
              <TextField
                type="email"
                label="Email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                fullWidth required autoFocus
                InputProps={{ sx: { color: 'white' } }}
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: goldMid },
                }}
              />
              <Button
                type="submit" variant="contained" disabled={lookingUp}
                sx={{ background: gold, color: dark, fontWeight: 700, py: 1.4, '&:hover': { background: '#e0be6a' } }}
              >
                {lookingUp ? <CircularProgress size={22} sx={{ color: dark }} /> : 'Continue'}
              </Button>
              {auth.currentUser && (
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
                  You're signed in. Your purchase will be attached to your account automatically.
                </Typography>
              )}
            </Stack>
          </form>
        )}

        {(step === 'newUser' || step === 'returningFullAuth') && (
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {step === 'returningFullAuth' && (
                <Alert severity="info" sx={{
                  '& .MuiAlert-message': { width: '100%' },
                }}>
                  <Typography sx={{ fontSize: '0.88rem', mb: 1 }}>
                    You appear to have an account with this email.
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" variant="contained" onClick={() => navigate('/login')}>
                      Sign in for faster checkout
                    </Button>
                    <Button size="small" variant="text" onClick={() => setConfirmReturning(true)}>
                      Continue as guest
                    </Button>
                  </Stack>
                </Alert>
              )}

              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.65)' }}>
                Email: <strong style={{ color: gold }}>{email}</strong>
                <Box component="span" onClick={() => { setStep('email'); setConfirmReturning(false); }}
                     sx={{ ml: 1, color: goldMid, cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline' }}>
                  change
                </Box>
              </Typography>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="First name" value={firstName} onChange={e => setFirstName(e.target.value)}
                  fullWidth required
                  InputProps={{ sx: { color: 'white' } }}
                  InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                  sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                />
                <TextField
                  label="Last name" value={lastName} onChange={e => setLastName(e.target.value)}
                  fullWidth required
                  InputProps={{ sx: { color: 'white' } }}
                  InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                  sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
                />
              </Box>

              <TextField
                type="tel" label="Phone number" value={phone} onChange={e => setPhone(e.target.value)}
                fullWidth required
                InputProps={{ sx: { color: 'white' } }}
                InputLabelProps={{ sx: { color: 'rgba(255,255,255,0.6)' } }}
                sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' } }}
              />

              {waiverDoc && (
                <Box sx={{
                  border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', p: 2,
                  background: 'rgba(0,0,0,0.2)',
                }}>
                  <Typography sx={{ fontSize: '0.8rem', color: gold, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', mb: 1 }}>
                    {waiverDoc.title || 'Waiver'}
                  </Typography>
                  <Box sx={{
                    maxHeight: 200, overflowY: 'auto', mb: 1,
                    fontSize: '0.78rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.5,
                    pr: 1,
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: waiverDoc.contentHtml || waiverDoc.content || '<em>Waiver content not available.</em>' }} />
                  </Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={waiverAccepted}
                        onChange={e => setWaiverAccepted(e.target.checked)}
                        sx={{ color: goldMid, '&.Mui-checked': { color: gold } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.9)' }}>
                      I have read and accept this waiver.
                    </Typography>}
                  />
                </Box>
              )}

              <Button
                type="submit" variant="contained"
                disabled={submitting || (waiverDoc && !waiverAccepted) || (step === 'returningFullAuth' && !confirmReturning)}
                sx={{
                  background: gold, color: dark, fontWeight: 700, py: 1.4,
                  '&:hover': { background: '#e0be6a' },
                  '&.Mui-disabled': { background: 'rgba(201,168,76,0.3)', color: 'rgba(13,30,53,0.5)' },
                }}
              >
                {submitting ? <CircularProgress size={22} sx={{ color: dark }} /> : `Continue to Payment — $${tier?.monthlyPrice}`}
              </Button>

              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                Secure payment powered by Stripe. Your day pass activates immediately upon successful payment.
              </Typography>
            </Stack>
          </form>
        )}
      </Box>
    </Box>
  );
}
